import React, { useState, useRef } from "react";
import { Calendar, MapPin, Clock, Music, ImageIcon, Tag, Upload, X, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CreateEvent = ({ isOpen, onClose, onEventCreated }) => {
  const [formData, setFormData] = useState({
    title: "",
    artist: "",
    date: "",
    time: "",
    venue: "",
    location: "",
    price: "",
    image: "",
    category: "Hip-Hop/RNB",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCategoryChange = (value) => {
    setFormData({
      ...formData,
      category: value,
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5MB");
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload to server
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);

      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.url) {
        // Convert relative URL to absolute URL
        const imageUrl = `${BACKEND_URL}${response.data.url}`;
        setFormData({
          ...formData,
          image: imageUrl,
        });
        toast.success("Image uploadée avec succès !");
      }
    } catch (error) {
      console.error("Erreur upload:", error);
      toast.error("Erreur lors de l'upload de l'image");
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, image: "" });
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title || !formData.artist || !formData.date || !formData.venue || !formData.price) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setLoading(true);

    try {
      const eventData = {
        ...formData,
        price: parseFloat(formData.price),
        image: formData.image || "https://images.unsplash.com/photo-1558618666-fcd25c85cd64",
      };

      await axios.post(`${API}/events`, eventData);
      
      toast.success("Événement créé avec succès !");
      
      // Reset form
      setFormData({
        title: "",
        artist: "",
        date: "",
        time: "",
        venue: "",
        location: "",
        price: "",
        image: "",
        category: "Hip-Hop/RNB",
        description: "",
      });
      setImagePreview(null);
      
      // Callback and close
      if (onEventCreated) {
        onEventCreated();
      }
      
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Erreur lors de la création:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-street tracking-wide">
            Créer un événement
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Title */}
          <div>
            <Label htmlFor="title">Titre de l'événement *</Label>
            <Input
              data-testid="event-title-input"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="AFROBEAT FEVER NIGHT"
              required
              className="mt-2"
            />
          </div>

          {/* Artist */}
          <div>
            <Label htmlFor="artist">Artiste / DJ *</Label>
            <Input
              data-testid="event-artist-input"
              id="artist"
              name="artist"
              value={formData.artist}
              onChange={handleInputChange}
              placeholder="DJ AFRO SUPREME"
              required
              className="mt-2"
            />
          </div>

          {/* Date & Time */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date">Date *</Label>
              <Input
                data-testid="event-date-input"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                placeholder="Samedi 15 Novembre 2025"
                required
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="time">Horaire</Label>
              <Input
                data-testid="event-time-input"
                id="time"
                name="time"
                value={formData.time}
                onChange={handleInputChange}
                placeholder="22h00 - 05h00"
                className="mt-2"
              />
            </div>
          </div>

          {/* Venue & Location */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="venue">Lieu *</Label>
              <Input
                data-testid="event-venue-input"
                id="venue"
                name="venue"
                value={formData.venue}
                onChange={handleInputChange}
                placeholder="Le Zenith Paris"
                required
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="location">Ville</Label>
              <Input
                data-testid="event-location-input"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="Paris, France"
                className="mt-2"
              />
            </div>
          </div>

          {/* Category & Price */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="category">Catégorie *</Label>
              <Select value={formData.category} onValueChange={handleCategoryChange}>
                <SelectTrigger className="mt-2" data-testid="event-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Hip-Hop/RNB">Hip-Hop/RNB</SelectItem>
                  <SelectItem value="Afrobeat">Afrobeat</SelectItem>
                  <SelectItem value="Dancehall/Caraïbes">Dancehall/Caraïbes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="price">Prix (€) *</Label>
              <Input
                data-testid="event-price-input"
                id="price"
                name="price"
                type="number"
                min="0"
                step="0.01"
                value={formData.price}
                onChange={handleInputChange}
                placeholder="35"
                required
                className="mt-2"
              />
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <Label>Image de l'événement</Label>
            <div className="mt-2 space-y-3">
              {/* Image Preview */}
              {(imagePreview || formData.image) && (
                <div className="relative w-full h-48 rounded-lg overflow-hidden bg-muted">
                  <img
                    src={imagePreview || formData.image}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2"
                    onClick={handleRemoveImage}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                  {uploading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Loader2 className="w-8 h-8 text-white animate-spin" />
                    </div>
                  )}
                </div>
              )}

              {/* Upload Button */}
              {!imagePreview && !formData.image && (
                <div
                  className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground mb-1">
                    Cliquez pour importer une image
                  </p>
                  <p className="text-xs text-muted-foreground">
                    JPEG, PNG, GIF ou WebP (max 5MB)
                  </p>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleImageUpload}
                className="hidden"
                data-testid="event-image-upload"
              />

              {/* Or use URL */}
              <div className="flex items-center gap-2">
                <div className="h-px bg-border flex-1" />
                <span className="text-xs text-muted-foreground">ou</span>
                <div className="h-px bg-border flex-1" />
              </div>

              <Input
                data-testid="event-image-input"
                name="image"
                value={formData.image}
                onChange={handleInputChange}
                placeholder="Coller une URL d'image..."
                className="text-sm"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              data-testid="event-description-input"
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Décrivez votre événement..."
              rows={4}
              className="mt-2"
            />
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={loading || uploading}
            >
              Annuler
            </Button>
            <Button
              data-testid="submit-event-btn"
              type="submit"
              disabled={loading || uploading}
              className="flex-1 bg-primary hover:bg-primary/90"
            >
              {loading ? "Création..." : "Créer l'événement"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
